import type { InsightColumnDefinition } from '../models/insight.types'

import type { ColumnSubType, ReportColumn, ReportRow } from '@/features/reports'

export interface InsightColumnAccessors {
  getColumn?: (field: string) => ReportColumn | null
  metricLabel?: (field: string) => string
}

const COLUMN_SUB_TYPES = new Set<ColumnSubType>([
  'badge',
  'window_cells',
  'currency',
  'numeric',
  'decimal',
  'percentage',
  'meta',
  'date',
  'datetime',
  'image',
  'wsName',
  'status',
  'array',
  'text',
])

const WIRE_NUMERIC_TYPES: Readonly<Record<string, ColumnSubType>> = Object.freeze({
  number: 'numeric',
  decimal: 'decimal',
})

function toColumnSubType(value: unknown): ColumnSubType | undefined {
  if (typeof value !== 'string') return undefined
  const folded = WIRE_NUMERIC_TYPES[value]
  if (folded !== undefined) return folded
  return COLUMN_SUB_TYPES.has(value as ColumnSubType) ? (value as ColumnSubType) : undefined
}

const COLUMN_SUB_TYPE_BY_FIELD: Readonly<Record<string, ColumnSubType>> = Object.freeze({
  peak_month: 'badge',
  last_year_same_window: 'window_cells',
})

const COLUMN_PRESENTATION: Readonly<Record<string, Record<string, unknown>>> = Object.freeze({
  weeks_to_peak: { warnBelow: 4 },
  matched_keywords_chips: { chips: true },
})

function tableColumn(
  definition: InsightColumnDefinition,
  frozen: ReadonlySet<string>,
  accessors: InsightColumnAccessors,
): ReportColumn {
  const subType = COLUMN_SUB_TYPE_BY_FIELD[definition.key] ?? toColumnSubType(definition.type)
  const catalogColumn = accessors.getColumn?.(definition.key)
  return {
    ...catalogColumn,
    field: definition.key,
    header:
      definition.label?.trim() ||
      catalogColumn?.header ||
      accessors.metricLabel?.(definition.key) ||
      definition.key,
    ...(subType ? { sub_type: subType } : {}),
    ...(subType === 'numeric' || subType === 'decimal' ? { numeric: true } : {}),
    ...(WIRE_MEDIA_TYPES.has(definition.type ?? '') ? { isImage: true } : {}),
    ...(subType === 'percentage' ? { numeric: true, percentage: true } : {}),
    ...(subType === 'currency' ? { numeric: true, currency: true } : {}),
    ...(COLUMN_PRESENTATION[definition.key] === undefined
      ? {}
      : { meta: { ...catalogColumn?.meta, ...COLUMN_PRESENTATION[definition.key] } }),
    ...(definition.sort ? { sortable: true } : {}),
    ...(definition.frozen || frozen.has(definition.key) ? { frozen: true } : {}),
  }
}

function isRenderableDefinition(
  definition: InsightColumnDefinition,
  frozen: ReadonlySet<string>,
): boolean {
  return definition.type !== 'url' || definition.frozen === true || frozen.has(definition.key)
}

function mergeDefinitions(
  configColumns: readonly InsightColumnDefinition[],
  elementColumns: readonly InsightColumnDefinition[],
): readonly InsightColumnDefinition[] {
  if (configColumns.length === 0) return elementColumns
  if (elementColumns.length === 0) return configColumns
  const byKey = new Map(elementColumns.map((column) => [column.key, column]))
  const configKeys = new Set(configColumns.map((column) => column.key))
  return [
    ...configColumns.map((column) => ({ ...column, ...byKey.get(column.key) })),
    ...elementColumns.filter((column) => !configKeys.has(column.key)),
  ]
}

const WIRE_MEDIA_TYPES = new Set(['image', 'asset_list'])

function isMedia(column: ReportColumn): boolean {
  return column.isImage === true || column.sub_type === 'image'
}

function orderColumns(columns: readonly ReportColumn[]): ReportColumn[] {
  return [...columns].sort((left, right) => {
    if (isMedia(left) !== isMedia(right)) return isMedia(left) ? -1 : 1
    if (!left.numeric && right.numeric) return -1
    if (left.numeric && !right.numeric) return 1
    return 0
  })
}

export function columnsFromRows(
  rows: readonly ReportRow[],
  accessors: InsightColumnAccessors,
): ReportColumn[] {
  const fields = new Set<string>()
  for (const row of rows) {
    for (const field of Object.keys(row)) fields.add(field)
  }
  return [...fields].map((field) => {
    const catalogColumn = accessors.getColumn?.(field)
    return {
      ...catalogColumn,
      field,
      header: catalogColumn?.header ?? accessors.metricLabel?.(field) ?? field,
    }
  })
}

export function resolveInsightColumns(
  configColumns: readonly InsightColumnDefinition[],
  elementColumns: readonly InsightColumnDefinition[],
  frozen: ReadonlySet<string>,
  rows: readonly ReportRow[],
  hidden: ReadonlySet<string>,
  accessors: InsightColumnAccessors,
): ReportColumn[] {
  const definitions = mergeDefinitions(configColumns, elementColumns)
  const renderable = definitions.filter((column) => isRenderableDefinition(column, frozen))
  return orderColumns(
    renderable.length > 0
      ? renderable.map((column) => tableColumn(column, frozen, accessors))
      : columnsFromRows(rows.slice(0, 1), accessors).filter((column) => !hidden.has(column.field)),
  )
}
